import { createSeedData } from '../data/seed'
import { getPublicationId, normalizePlanStatus, normalizeTemplatePlatforms, publicationStatusFromPlan } from '../shared/utils/publications'
import { getDefaultPermissions, getMemberProjectIds, projectPermissionKeys } from '../shared/utils/permissions'

const storageKey = 'matrix-content-os:prototype:v3'
const backupStorageKey = 'matrix-content-os:local-backup-before-backend:v1'
const migrationMarkerKey = 'matrix-content-os:backend-migrated:v1'

function extractReferenceTags(content) {
  if (typeof content !== 'string') return []
  const normalized = content.replace(/[\u200b-\u200f\ufeff]/g, '')
  const matches = [...normalized.matchAll(/#([^#\n]{1,40})#/g)].map(match => match[1].trim())
  if (!matches.length) matches.push(...[...normalized.matchAll(/(?:^|\s)#([^#\s]{1,40})/g)].map(match => match[1].trim()))
  return [...new Set(matches.filter(Boolean))]
}

function normalizeState(state) {
  const defaults = new Map(createSeedData().skus.map(sku => [sku.id, sku.dailyTarget]))
  const skuMap = new Map(state.skus.map(sku => [sku.id, sku]))
  const productIdSet = new Set(state.products.map(product => product.id))
  const rawAccountMap = new Map(state.accounts.map(account => [account.id, account]))
  const referenceMap = new Map(state.references.map(reference => [reference.id, reference]))
  const plannedAccountMap = new Map(state.plans.map(plan => [plan.assetId, plan.accountId]))
  const normalizedTemplates = state.templates.map(({ skuIds, ...template }) => ({
    ...template,
    skuId: template.skuId || skuIds?.[0] || '',
    platforms: (() => {
      const referencePlatforms = (template.referenceIds || []).map(referenceId => referenceMap.get(referenceId)?.platform)
      const planPlatforms = state.plans.filter(plan => plan.templateId === template.id).map(plan => rawAccountMap.get(plan.accountId)?.platform)
      const settingPlatforms = (state.publishSettings || []).filter(setting => (setting.rules || []).some(rule => rule.templateId === template.id)).map(setting => rawAccountMap.get(setting.accountId)?.platform)
      const platforms = normalizeTemplatePlatforms([...(template.platforms || []), ...referencePlatforms, ...planPlatforms, ...settingPlatforms])
      return platforms.length ? platforms : ['xiaohongshu', 'douyin', 'wechat']
    })(),
  }))
  const templateMap = new Map(normalizedTemplates.map(template => [template.id, template]))
  const derivedProductIds = new Map(state.accounts.map(account => [account.id, new Set()]))
  const addTemplateProduct = (accountId, templateId) => {
    const productId = skuMap.get(templateMap.get(templateId)?.skuId)?.productId
    if (accountId && productId) derivedProductIds.get(accountId)?.add(productId)
  }
  state.assets.forEach(asset => addTemplateProduct(asset.accountId || plannedAccountMap.get(asset.id), asset.templateId))
  state.plans.forEach(plan => addTemplateProduct(plan.accountId, plan.templateId))
  ;(state.publishSettings || []).forEach(setting => (setting.rules || []).forEach(rule => addTemplateProduct(setting.accountId, rule.templateId)))
  const normalizedAccounts = state.accounts.map(account => ({
    ...account,
    productIds: (Array.isArray(account.productIds) ? account.productIds : [...(derivedProductIds.get(account.id) || [])]).filter(productId => productIdSet.has(productId)),
  }))
  const normalizedMembers = state.members.map(member => {
    const projectIds = member.role === 'owner'
      ? state.projects.map(project => project.id)
      : [...new Set((Array.isArray(member.projectIds) ? member.projectIds : member.projectId ? [member.projectId] : []).filter(projectId => state.projects.some(project => project.id === projectId)))]
    const permissionsByProject = { ...(member.permissionsByProject || {}) }
    projectIds.forEach(projectId => {
      permissionsByProject[projectId] = member.role === 'owner'
        ? projectPermissionKeys
        : [...new Set((permissionsByProject[projectId] || getDefaultPermissions(member.role)).filter(permission => projectPermissionKeys.includes(permission)))]
    })
    return { ...member, projectId: member.projectId || projectIds[0] || '', projectIds, permissionsByProject }
  })
  const currentMemberId = normalizedMembers.some(member => member.id === state.currentMemberId)
    ? state.currentMemberId
    : normalizedMembers.find(member => member.role === 'owner')?.id || normalizedMembers[0]?.id || ''
  const currentMember = normalizedMembers.find(member => member.id === currentMemberId)
  const accessibleProjectIds = getMemberProjectIds(currentMember, state.projects)
  const currentProjectId = accessibleProjectIds.includes(state.currentProjectId) ? state.currentProjectId : accessibleProjectIds[0] || state.projects[0]?.id || ''
  const normalizedAssets = state.assets.map(asset => ({
    ...asset,
    skuId: templateMap.get(asset.templateId)?.skuId || asset.skuId,
    accountId: Object.hasOwn(asset, 'accountId') ? asset.accountId : plannedAccountMap.get(asset.id) || '',
    status: asset.status === 'published' ? 'published' : 'unpublished',
  }))
  const assetMap = new Map(normalizedAssets.map(asset => [asset.id, asset]))
  const normalizedPlans = state.plans.map(plan => {
    const template = templateMap.get(plan.templateId)
    const asset = assetMap.get(plan.assetId)
    const skuId = template?.skuId || plan.skuId || asset?.skuId || ''
    const assetMatchesPlan = asset?.templateId === plan.templateId && asset?.skuId === skuId
    return { ...plan, skuId, platform: rawAccountMap.get(plan.accountId)?.platform || plan.platform || '', assetId: assetMatchesPlan ? plan.assetId : null, status: normalizePlanStatus(plan.status) }
  })
  const existingSettingMap = new Map((state.publishSettings || []).map(setting => [setting.accountId, setting]))
  const publishSettings = normalizedAccounts.map(account => {
    const existing = existingSettingMap.get(account.id)
    const dailyPlanCounts = new Map()
    normalizedPlans.filter(plan => {
      const productId = skuMap.get(templateMap.get(plan.templateId)?.skuId)?.productId
      return plan.accountId === account.id && account.productIds.includes(productId) && templateMap.get(plan.templateId)?.platforms.includes(account.platform)
    }).forEach(plan => {
      const dateKey = `${plan.date}:${plan.templateId}`
      dailyPlanCounts.set(dateKey, (dailyPlanCounts.get(dateKey) || 0) + 1)
    })
    const derivedRuleCounts = new Map()
    dailyPlanCounts.forEach((count, dateKey) => {
      const templateId = dateKey.slice(dateKey.indexOf(':') + 1)
      derivedRuleCounts.set(templateId, Math.max(derivedRuleCounts.get(templateId) || 0, count))
    })
    const existingRules = (existing?.rules || []).filter(rule => {
      const productId = skuMap.get(templateMap.get(rule.templateId)?.skuId)?.productId
      const template = templateMap.get(rule.templateId)
      return template && account.productIds.includes(productId) && template.platforms.includes(account.platform)
    }).map(rule => ({ templateId: rule.templateId, dailyCount: Math.max(0, Number(rule.dailyCount) || 0) }))
    const ruleMap = new Map(existingRules.map(rule => [rule.templateId, rule.dailyCount]))
    derivedRuleCounts.forEach((dailyCount, templateId) => {
      if (!ruleMap.has(templateId)) ruleMap.set(templateId, dailyCount)
    })
    if (existing) return {
      ...existing,
      id: existing.id || `publish-setting-${account.id}`,
      projectId: account.projectId,
      accountId: account.id,
      startTime: existing.startTime || '07:00',
      endTime: existing.endTime || '23:00',
      distributionMode: existing.distributionMode === 'uniform' ? 'uniform' : 'smart',
      rules: [...ruleMap].map(([templateId, dailyCount]) => ({ templateId, dailyCount })),
    }
    return {
      id: `publish-setting-${account.id}`,
      projectId: account.projectId,
      accountId: account.id,
      startTime: '07:00',
      endTime: '23:00',
      distributionMode: 'smart',
      rules: [...ruleMap].map(([templateId, dailyCount]) => ({ templateId, dailyCount })),
    }
  })
  const publicationMap = new Map()
  const mergePublication = publication => {
    if (!publication.assetId || !publication.platform || !assetMap.has(publication.assetId)) return
    const id = getPublicationId(publication.assetId, publication.platform)
    const current = publicationMap.get(id)
    if (current?.status === 'published' && publication.status !== 'published') return
    publicationMap.set(id, {
      ...current,
      ...publication,
      id,
      projectId: assetMap.get(publication.assetId).projectId,
      status: ['published', 'canceled', 'failed'].includes(publication.status) ? publication.status : 'scheduled',
    })
  }
  ;(state.assetPublications || []).forEach(mergePublication)
  normalizedPlans.forEach(plan => {
    if (!plan.assetId || !plan.platform) return
    mergePublication({
      assetId: plan.assetId,
      platform: plan.platform,
      accountId: plan.accountId,
      planId: plan.id,
      status: publicationStatusFromPlan(plan.status),
      scheduledAt: plan.date ? `${plan.date}T${plan.time || '00:00'}:00` : '',
      publishedAt: plan.status === 'published' ? plan.publishedAt || '' : '',
    })
  })
  normalizedAssets.forEach(asset => {
    const account = rawAccountMap.get(asset.accountId)
    if (asset.status !== 'published' || !account?.platform) return
    mergePublication({ assetId: asset.id, platform: account.platform, accountId: account.id, status: 'published' })
  })
  return {
    ...state,
    version: 5,
    currentProjectId,
    currentMemberId,
    members: normalizedMembers,
    accounts: normalizedAccounts,
    skus: state.skus.map(({ price, stock, status, spec, ...sku }) => ({
      ...sku,
      dailyTarget: Number(sku.dailyTarget ?? defaults.get(sku.id) ?? 2),
    })),
    references: state.references.map(({ views, ...reference }) => ({
      ...reference,
      comments: Number(reference.comments ?? 0),
      favorites: Number(reference.favorites ?? reference.comments ?? 0),
      content: typeof reference.content === 'string' ? reference.content : '',
      tags: Array.isArray(reference.tags) && reference.tags.length ? reference.tags : extractReferenceTags(reference.content),
    })),
    templates: normalizedTemplates,
    assets: normalizedAssets,
    assetPublications: [...publicationMap.values()],
    plans: normalizedPlans,
    publishSettings,
  }
}

export const appRepository = {
  normalize: normalizeState,
  load() {
    try {
      const value = window.localStorage.getItem(storageKey)
      if (!value) return createSeedData()
      const parsed = JSON.parse(value)
      return [3, 4, 5].includes(parsed.version) ? normalizeState(parsed) : createSeedData()
    } catch {
      return createSeedData()
    }
  },
  save(state) {
    window.localStorage.setItem(storageKey, JSON.stringify(state))
  },
  hasStoredState() {
    return Boolean(window.localStorage.getItem(storageKey))
  },
  backup() {
    const value = window.localStorage.getItem(storageKey)
    if (value && !window.localStorage.getItem(backupStorageKey)) window.localStorage.setItem(backupStorageKey, value)
  },
  loadBackup() {
    try {
      const value = window.localStorage.getItem(backupStorageKey)
      if (!value) return null
      const parsed = JSON.parse(value)
      return [3, 4, 5].includes(parsed.version) ? normalizeState(parsed) : null
    } catch {
      return null
    }
  },
  needsBackendMigration() {
    return this.hasStoredState() && !window.localStorage.getItem(migrationMarkerKey)
  },
  markBackendMigration() {
    window.localStorage.setItem(migrationMarkerKey, new Date().toISOString())
  },
}
